import { ApiClient } from './base/apiClient';
import type {
  GetDeliveryCompanyRequest,
  GetDeliveryCompanyResponse,
} from '~/types/api/master/getDeliveryCompany';
import type { ApiResponse } from '~/types/api/common/apiResponse';
import type { GetOrganizationRequest, GetOrganizationResponse } from '~/types/api/master/getOrganization';
import type { GetStationRequest, GetStationResponse } from '~/types/api/master/getStation';
import type { GetAffiliationStationResponse } from '~/types/api/master/getAffiliationStation';
import type {
  GetUserCompanyRequest,
  GetUserCompanyResponse,
} from '~/types/api/master/getUserCompany';
import type {
  GetDriverMessageRequest,
  GetDriverMessageResponse,
} from '~/types/api/master/getDriverMessage';
import type { GetCodeListRequest, GetCodeListResponse } from '~/types/api/master/getCodeList';
import type {
  GetNearestStationRequest,
  GetNearestStationResponse,
} from '~/types/api/master/getNearestStation';
import type { GetContainerTypeResponse } from '~/types/api/master/getContainerType';
import type { GetWorkTypeResponse } from '~/types/api/master/getWorkType';
import type { GetUiMessagesResponse } from '~/types/api/master/getUiMessages';
import type { GetRolesResponse } from '~/types/api/master/getRoles';
import type { GetAccountTypeResponse } from '~/types/api/master/getAccountType';
import type { GetRoleEditScopesResponse } from '~/types/api/master/getRoleEditScopes';
import type { GetRoleApproveScopesResponse } from '~/types/api/master/getRoleApproveScopes';
import type { GetSelectableRolesResponse } from '~/types/api/master/getSelectableRoles';

const BASE_PATH = '/master';

/**
 * マスタデータ取得に関するリポジトリ
 */
export class MasterRepository {
  private apiClient: ApiClient;

  constructor(displayId: string) {
    this.apiClient = new ApiClient(displayId);
  }

  /**
   * [sp-api-101] 組織リスト取得API
   *
   * @returns 組織リスト
   */
  async getOrganizations(request: GetOrganizationRequest): Promise<ApiResponse<GetOrganizationResponse>> {
    const query = new URLSearchParams();
    if (request.organizationKbn !== undefined) {
      query.append('organizationKbn', request.organizationKbn);
    }
    return await this.apiClient.get<GetOrganizationResponse>(BASE_PATH + '/organization', query);
  }

  /**
   * [sp-api-102] 駅リスト取得API
   *
   * @param request リクエスト
   * @returns 駅リスト
   */
  async getStations(request: GetStationRequest): Promise<ApiResponse<GetStationResponse>> {
    const query = new URLSearchParams();
    if (request.organizationCd !== undefined) {
      query.append('organizationCd', request.organizationCd);
    }
    if (request.branchCd !== undefined) {
      query.append('branchCd', request.branchCd);
    }
    if (request.salesCd !== undefined) {
      query.append('salesCd', request.salesCd);
    }
    return await this.apiClient.get<GetStationResponse>(BASE_PATH + '/station', query);
  }

  /**
   * [sp-api-103] 利用者リスト取得API
   *
   * @param request リクエスト
   * @returns レスポンス
   */
  async getUserCompanies(
    request: GetUserCompanyRequest,
  ): Promise<ApiResponse<GetUserCompanyResponse>> {
    const query = new URLSearchParams();
    if (request.stationCd !== undefined) {
      query.append('stationCd', request.stationCd);
    }
    if (request.stationSubCd !== undefined) {
      query.append('stationSubCd', request.stationSubCd);
    }
    if (request.userCompanyCd !== undefined) {
      query.append('userCompanyCd', request.userCompanyCd);
    }

    return await this.apiClient.get<GetUserCompanyResponse>(BASE_PATH + '/user_company', query);
  }

  /**
   * [sp-api-104] 集配人リスト取得API
   *
   * @param request リクエスト
   * @returns レスポンス
   */
  async getDeliveryCompanies(
    request: GetDeliveryCompanyRequest,
  ): Promise<ApiResponse<GetDeliveryCompanyResponse>> {
    const query = new URLSearchParams();
    if (request.stationCd !== undefined) {
      query.append('stationCd', request.stationCd);
    }
    if (request.stationSubCd !== undefined) {
      query.append('stationSubCd', request.stationSubCd);
    }
    if (request.deliveryCompanyCd !== undefined) {
      query.append('deliveryCompanyCd', request.deliveryCompanyCd);
    }
    if (request.userCompanyCd !== undefined) {
      query.append('userCompanyCd', request.userCompanyCd);
    }

    return await this.apiClient.get<GetDeliveryCompanyResponse>(
      BASE_PATH + '/delivery_company',
      query,
    );
  }

  /**
   * [sp-api-105] トラックドライバーメッセージ取得API
   *
   * @param request リクエスト
   * @returns レスポンス
   */
  async getDriverMessage(
    request: GetDriverMessageRequest,
  ): Promise<ApiResponse<GetDriverMessageResponse>> {
    const query = new URLSearchParams();
    if (request.messageId !== undefined) {
      query.append('messageId', request.messageId);
    }

    return await this.apiClient.get<GetDriverMessageResponse>(
      BASE_PATH + '/driver_message',
      query,
    );
  };

  /**
   * [sp-api-106] コードリスト取得API
   *
   * @param request リクエスト
   * @returns レスポンス
   */
  async getCodeList(
    request: GetCodeListRequest,
  ): Promise<ApiResponse<GetCodeListResponse>> {
    const query = new URLSearchParams();
    if (request.codeType !== undefined) {
      query.append('codeType', request.codeType);
    }
    if (request.codeValue !== undefined) {
      query.append('codeValue', request.codeValue);
    }
    if (request.codeName !== undefined) {
      query.append('codeName', request.codeName);
    }

    return await this.apiClient.get<GetCodeListResponse>(BASE_PATH + '/code', query);
  }

  /**
   * [sp-api-107] 最寄り駅取得API
   *
   * @param request リクエスト
   * @returns レスポンス
   */
  async getNearestStation(
    request: GetNearestStationRequest,
  ): Promise<ApiResponse<GetNearestStationResponse>> {
    const query = new URLSearchParams();
    query.append('lat', String(request.lat));
    query.append('lon', String(request.lon));

    return await this.apiClient.get<GetNearestStationResponse>(BASE_PATH + '/station/near', query);
  }

  /**
   * [sp-api-108] コンテナ形式取得API
   *
   * @returns コンテナ形式リスト
   */
  async getContainerType(): Promise<ApiResponse<GetContainerTypeResponse>> {
    return await this.apiClient.get<GetContainerTypeResponse>(BASE_PATH + '/container_type');
  }

  /**
   * [sp-api-109] 作業種別取得API
   *
   * @returns 作業種別一覧
   */
  async getWorkType(): Promise<ApiResponse<GetWorkTypeResponse>> {
    return await this.apiClient.get<GetWorkTypeResponse>(BASE_PATH + '/work_type');
  }

  /**
   * [sp-api-110] 画面メッセージ取得API
   *
   * メッセージコード未指定の場合は全件取得
   *
   * @param messageCd メッセージコード
   * @returns 画面メッセージ一覧
   */
  async getUiMessages(messageCd?: string): Promise<ApiResponse<GetUiMessagesResponse>> {
    const query = new URLSearchParams();
    if (messageCd !== undefined) {
      query.append('messageCd', messageCd);
    }

    return await this.apiClient.get<GetUiMessagesResponse>(BASE_PATH + '/message/ui', query);
  }

  /**
   * [sp-api-111] 権限一覧取得API
   *
   * @returns 権限一覧
   */
  async getRoles(): Promise<ApiResponse<GetRolesResponse>> {
    return await this.apiClient.get<GetRolesResponse>(BASE_PATH + '/roles');
  }

  /**
   * [sp-api-112] アカウント編集範囲取得API
   *
   * @returns アカウント編集範囲
   */
  async getRoleEditScopes(): Promise<ApiResponse<GetRoleEditScopesResponse>> {
    return await this.apiClient.get<GetRoleEditScopesResponse>(BASE_PATH + '/role-edit-scopes');
  }

  /**
   * [sp-api-113] アカウント承認範囲取得API
   *
   * @returns アカウント承認範囲
   */
  async getRoleApprovalScopes(): Promise<ApiResponse<GetRoleApproveScopesResponse>> {
    return await this.apiClient.get<GetRoleApproveScopesResponse>(BASE_PATH + '/role-approve-scopes');
  }

  /**
   * [sp-api-114] アカウント区分取得API
   *
   * @returns アカウント区分一覧
   */
  async getAccountTypes(): Promise<ApiResponse<GetAccountTypeResponse>> {
    return await this.apiClient.get<GetAccountTypeResponse>(BASE_PATH + '/account-type');
  }

  /**
   * [sp-api-115] 権限選択リスト取得API
   *
   * @returns 権限選択リスト
   */
  async getSelectableRoles(): Promise<ApiResponse<GetSelectableRolesResponse>> {
    return await this.apiClient.get<GetSelectableRolesResponse>(BASE_PATH + '/selectable-roles');
  }

  /**
   * [sp-api-116] 所属駅リスト取得API
   *
   * @returns 所属駅リスト
   */
  async getAffiliationStations(): Promise<ApiResponse<GetAffiliationStationResponse>> {
    return await this.apiClient.get<GetAffiliationStationResponse>(BASE_PATH + '/station/affiliation');
  }
}
